import React, { useState, useEffect } from 'react';
import { getStoredToken } from '../services/authSession';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Shield, AlertTriangle, CheckCircle, Database, Calculator, FileText,
  Activity, RefreshCw, Building2, Lock, User, ChevronDown, ChevronUp
} from 'lucide-react';
import API from '../services/apiBase';
import { useClient } from '../context/ClientContext';

const DASHBOARD_TIMEOUT_MS = 20000;

const ExecutiveDashboard = ({ onNavigate }) => {
  const { clientId, clientName, hasActiveClient, loading: clientLoading } = useClient();
  const [dashboardData, setDashboardData] = useState(null);
  const [icsData, setIcsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [expandedDivision, setExpandedDivision] = useState(null);
  const [scheduleAExpanded, setScheduleAExpanded] = useState(false);
  const [selectedScheduleASection, setSelectedScheduleASection] = useState(null);

  // Get auth token from localStorage
  const getAuthToken = () => {
    return getStoredToken();
  };

  // Get clientId - prefer context but fallback to localStorage
  const effectiveClientId = clientId || localStorage.getItem('active_client_id');

  useEffect(() => {
    console.log('[Dashboard] clientId:', clientId, 'effectiveClientId:', effectiveClientId, 'clientLoading:', clientLoading);
    if (effectiveClientId) {
      console.log('[Dashboard] Fetching dashboard data for client:', effectiveClientId);
      // Clear stale state on client change
      setDashboardData(null);
      setIcsData(null);
      fetchDashboardData();
      fetchIcsData();
    } else if (!clientLoading) {
      setLoading(false);
    }
  }, [clientId, effectiveClientId, clientLoading]);

  const fetchDashboardData = async () => {
    const currentClientId = clientId || localStorage.getItem('active_client_id');
    
    // Don't fetch without a client selected
    if (!currentClientId) {
      console.log('[Dashboard] No clientId, skipping fetch');
      setLoading(false);
      return;
    }
    
    let timeoutId = null;
    try {
      setLoading(true);
      setRefreshing(true);
      setAccessDenied(false);
      setError(null);
      const controller = new AbortController();
      timeoutId = window.setTimeout(() => controller.abort(), DASHBOARD_TIMEOUT_MS);
      
      const token = getAuthToken();
      console.log('[Dashboard] Token available:', !!token);
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add auth token if available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Add client_id to the query
      const url = `${API}/executive-dashboard/summary?client_id=${currentClientId}`;
      console.log('[Dashboard] Fetching from:', url);
      
      const response = await fetch(url, { headers, signal: controller.signal });
      
      // Handle authorization errors
      if (response.status === 401) {
        setAccessDenied(true);
        setError('Authentication required. Please log in.');
        return;
      }
      
      if (response.status === 403) {
        setAccessDenied(true);
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Access denied. Insufficient permissions to view Executive Dashboard.');
        return;
      }
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      console.log('[Dashboard] Received data:', data ? 'yes' : 'no', 'rate_summary:', data?.rate_summary?.data_source);
      console.log('[Dashboard] ics_adequacy raw:', JSON.stringify(data?.ics_adequacy));
      setDashboardData(data);
      setUserInfo(data.user || null);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      if (err?.name === 'AbortError') {
        setError('Some dashboard sections timed out. Audit Readiness data is still loading.');
        // Set empty dashboardData so the page renders (icsData loads independently)
        setDashboardData(prev => prev || {});
      } else {
        setError(err?.message || 'Failed to load executive dashboard');
        setDashboardData(prev => prev || {});
      }
    } finally {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchIcsData = async () => {
    const currentClientId = clientId || localStorage.getItem('active_client_id');
    if (!currentClientId) return;
    try {
      const token = getAuthToken();
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API}/executive-dashboard/ics-adequacy?client_id=${currentClientId}`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      console.log('[Dashboard] ics_adequacy direct:', JSON.stringify(data));
      setIcsData(data);
    } catch (e) {
      console.error('[Dashboard] ics fetch error:', e);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '$0';
    if (Math.abs(value) >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  // Access Denied UI
  if (accessDenied) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <p className="text-sm text-gray-500 mb-4">
                The Executive Dashboard is available to users with appropriate permissions.
              </p>
              <button
                onClick={() => onNavigate?.('login')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Sign In
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No client selected warning - but only after client context has finished loading
  const hasAnyClient = effectiveClientId || hasActiveClient();
  if (!clientLoading && !hasAnyClient) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Client Selected</h3>
              <p className="text-gray-600 mb-4">
                Please select a client from the sidebar to view their dashboard data.
              </p>
              <button
                onClick={() => onNavigate?.('clients')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Go to Clients
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state - either client context loading or dashboard data loading
  if ((clientLoading || loading) && !dashboardData && !icsData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
          <p className="mt-2 text-gray-600">Loading Executive Dashboard...</p>
        </div>
      </div>
    );
  }

  const { rate_summary, division_rates, gl_summary, contract_summary, recent_activity, schedule_a, gl_reconciliation } = dashboardData || {};
  // Use direct ICS fetch as source of truth (independent of slow summary endpoint)
  const ics_adequacy = icsData || dashboardData?.ics_adequacy;

  // Get division data with fallback to overall rates
  const divisions = division_rates?.divisions || [];
  // Use rates from rate_summary as primary source
  const fringeRate = rate_summary?.fringe_rate || 0;
  const gaRate = rate_summary?.ga_rate || 0;
  

  return (
    <div className="space-y-6 w-full px-0 mx-0" style={{ maxWidth: '100%' }}>
      {/* Active Client Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-sm text-blue-600 font-medium">Active Client</p>
            <p className="text-lg font-bold text-gray-900">{clientName}</p>
          </div>
        </div>
        <Badge className="bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3 mr-1" />
          Data Loaded
        </Badge>
      </div>

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Executive Dashboard</h2>
          <p className="text-sm text-gray-500">
            AI-enabled incurred cost reporting
            {rate_summary?.calculation_date && (
              <span className="ml-2 text-xs text-gray-400">
                • Last calc: {new Date(rate_summary.calculation_date).toLocaleDateString()}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {userInfo && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
              <User className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">{userInfo.name || userInfo.email}</span>
              <Badge className="bg-blue-100 text-blue-700 text-xs">
                {userInfo.role?.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>
          )}
          <button
            onClick={() => { fetchDashboardData(); fetchIcsData(); }}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">Error loading dashboard: {error}</span>
        </div>
      )}

      {/* DCAA Audit Readiness & Schedule A Summary — stacked full width */}
      <div className="flex flex-col gap-4">
        {/* DCAA Audit Readiness Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className={`w-5 h-5 ${
                ics_adequacy?.audit_status === 'ready' ? 'text-green-600' :
                ics_adequacy?.audit_status === 'not_ready' ? 'text-red-600' : 'text-yellow-600'
              }`} />
              Schedule Reconciliations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="py-1">
              {/* Score + Badge row */}
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className={`text-4xl font-bold leading-none ${
                    ics_adequacy?.audit_status === 'ready' ? 'text-green-600' :
                    ics_adequacy?.audit_status === 'needs_review' ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {ics_adequacy?.overall_score ?? 0}%
                  </div>
                  <div className={`text-xl font-semibold mt-1 ${
                    ics_adequacy?.audit_status === 'ready' ? 'text-green-600' :
                    ics_adequacy?.audit_status === 'needs_review' ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {ics_adequacy?.passed ?? 0}/{ics_adequacy?.total ?? 0}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">pools tying</div>
                </div>
                <Badge className={`text-sm px-3 py-1 font-semibold ${
                  ics_adequacy?.audit_status === 'ready' ? 'bg-green-100 text-green-800' :
                  ics_adequacy?.audit_status === 'needs_review' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {ics_adequacy?.audit_status === 'ready' ? '✓ Audit Ready' :
                   ics_adequacy?.audit_status === 'needs_review' ? '⚠ Needs Attention' :
                   '✗ Not Ready'}
                </Badge>
              </div>

              {/* Progress bar — color matches status */}
              <Progress
                value={ics_adequacy?.overall_score ?? 0}
                indicatorColor={
                  ics_adequacy?.audit_status === 'ready' ? 'bg-green-500' :
                  ics_adequacy?.audit_status === 'needs_review' ? 'bg-yellow-400' : 'bg-red-500'
                }
                className="mb-3"
              />

              {/* Per-pool tie-out: Schedule A dashboard total vs Schedule C/B/B-1 export total */}
              <div className="overflow-x-auto">
                {(ics_adequacy?.checks || []).length === 0 ? (
                  <div className="text-xs text-gray-400 italic text-center py-2">No pool data available</div>
                ) : (
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-200">
                        <th className="py-1.5 pr-2 font-medium">Pool</th>
                        <th className="py-1.5 px-2 font-medium text-right">Schedule A Pool Total</th>
                        <th className="py-1.5 px-2 font-medium text-right">Schedule C Total</th>
                        <th className="py-1.5 pl-2 font-medium text-center">Ties?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ics_adequacy.checks.map((check) => (
                        <tr key={check.id} className={`border-b border-gray-100 ${check.status ? '' : 'bg-red-50'}`}>
                          <td className={`py-1.5 pr-2 font-medium ${check.status ? 'text-gray-700' : 'text-red-700'}`}>
                            {check.label}
                          </td>
                          <td className="py-1.5 px-2 text-right tabular-nums text-gray-600">
                            {(check.schedule_a_total ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                          </td>
                          <td className="py-1.5 px-2 text-right tabular-nums text-gray-600">
                            {(check.schedule_c_total ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                          </td>
                          <td className="py-1.5 pl-2 text-center">
                            {check.status
                              ? <CheckCircle className="w-3.5 h-3.5 text-green-500 inline-block" />
                              : (
                                <button
                                  onClick={() => onNavigate?.('audit')}
                                  className="inline-flex items-center gap-1 text-red-600 hover:text-red-700"
                                  title={check.detail}
                                >
                                  <AlertTriangle className="w-3.5 h-3.5" />
                                </button>
                              )
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Rate verification: stated rate recomputes as pool / base */}
              {(ics_adequacy?.rate_checks || []).length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-semibold text-gray-600 mb-1">
                    Rate Verification
                    <span className="ml-2 font-normal text-gray-400">
                      {ics_adequacy?.rate_passed ?? 0}/{ics_adequacy?.rate_total ?? 0} rates recompute
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="text-left text-gray-500 border-b border-gray-200">
                          <th className="py-1.5 pr-2 font-medium">Pool</th>
                          <th className="py-1.5 px-2 font-medium text-right">Pool</th>
                          <th className="py-1.5 px-2 font-medium text-right">Base</th>
                          <th className="py-1.5 px-2 font-medium text-right">Rate (Pool ÷ Base)</th>
                          <th className="py-1.5 pl-2 font-medium text-center">Recomputes?</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ics_adequacy.rate_checks.map((check) => (
                          <tr key={check.id} className={`border-b border-gray-100 ${check.status ? '' : 'bg-red-50'}`}>
                            <td className={`py-1.5 pr-2 font-medium ${check.status ? 'text-gray-700' : 'text-red-700'}`}>
                              {check.label}
                            </td>
                            <td className="py-1.5 px-2 text-right tabular-nums text-gray-600">
                              {(check.pool ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                            </td>
                            <td className="py-1.5 px-2 text-right tabular-nums text-gray-600">
                              {(check.base ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                            </td>
                            <td className="py-1.5 px-2 text-right tabular-nums text-gray-600">
                              {(check.computed_rate ?? 0).toFixed(2)}%
                            </td>
                            <td className="py-1.5 pl-2 text-center">
                              {check.status
                                ? <CheckCircle className="w-3.5 h-3.5 text-green-500 inline-block" />
                                : <span title={check.detail}><AlertTriangle className="w-3.5 h-3.5 text-red-600 inline-block" /></span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Audit message */}
              {ics_adequacy?.audit_message && (
                <div className={`mt-3 text-xs font-medium text-center py-1.5 rounded ${
                  ics_adequacy?.audit_status === 'ready' ? 'bg-green-100 text-green-700' :
                  ics_adequacy?.audit_status === 'needs_review' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {ics_adequacy.audit_message}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Schedule A Summary Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5 text-blue-600" />
              Schedule A - Rate Summary
              {schedule_a?.data_source === 'gl_calculation_runs' && (
                <Badge className="ml-2 bg-green-100 text-green-700 text-xs">Live Data</Badge>
              )}
              {schedule_a?.sections?.length > 5 && (
                <span className="ml-auto text-xs text-gray-500 font-normal">
                  {scheduleAExpanded ? `Showing all ${schedule_a.sections.length} sections` : `Showing 5 of ${schedule_a.sections.length} sections`}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-2 px-3 font-semibold text-gray-700 w-8"></th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Section</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">Pool</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">Base</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {(scheduleAExpanded ? schedule_a?.sections : schedule_a?.sections?.slice(0, 5))?.map((section, idx) => {
                    const isSelected = selectedScheduleASection === section.key;
                    return (
                      <React.Fragment key={idx}>
                        <tr
                          className={`border-b cursor-pointer transition-colors ${section.key === 'ga' ? 'bg-blue-50 font-semibold' : 'hover:bg-gray-50'} ${isSelected ? 'bg-indigo-50' : ''}`}
                          onClick={() => setSelectedScheduleASection(isSelected ? null : section.key)}
                        >
                          <td className="py-2 px-3 text-gray-400">
                            {isSelected ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </td>
                          <td className="py-2 px-3">{section.label}</td>
                          <td className="py-2 px-3 text-right">{formatCurrency(section.pool)}</td>
                          <td className="py-2 px-3 text-right">
                            {section.headcount_base
                              ? Math.round(section.base).toLocaleString()
                              : formatCurrency(section.base)}
                          </td>
                          <td className="py-2 px-3 text-right font-medium text-blue-600">
                            {section.headcount_base
                              ? `$${section.rate?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/HC`
                              : `${section.rate?.toFixed(2)}%`}
                          </td>
                        </tr>
                        {/* Drill-down detail row */}
                        {isSelected && (
                          <tr className="bg-indigo-50/50">
                            <td colSpan="5" className="px-3 py-3">
                              <div className="bg-white rounded-lg p-4 border border-indigo-100">
                                <div className="text-xs font-semibold text-indigo-800 mb-3">Rate Calculation Breakdown</div>
                                <div className="grid grid-cols-4 gap-4 text-center">
                                  <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="text-xs text-gray-500 mb-1">Pool Amount</div>
                                    <div className="text-lg font-bold text-gray-800">{formatCurrency(section.pool)}</div>
                                    <div className="text-xs text-gray-400">Total indirect costs</div>
                                  </div>
                                  <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="text-xs text-gray-500 mb-1">÷</div>
                                    <div className="text-lg font-bold text-gray-600">/</div>
                                  </div>
                                  <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="text-xs text-gray-500 mb-1">Base Amount</div>
                                    <div className="text-lg font-bold text-gray-800">
                                      {section.headcount_base
                                        ? Math.round(section.base).toLocaleString()
                                        : formatCurrency(section.base)}
                                    </div>
                                    <div className="text-xs text-gray-400">{section.headcount_base ? 'Headcount' : 'Allocation base'}</div>
                                  </div>
                                  <div className="bg-indigo-100 rounded-lg p-3 border border-indigo-200">
                                    <div className="text-xs text-indigo-600 mb-1">= Rate</div>
                                    <div className="text-lg font-bold text-indigo-700">
                                      {section.headcount_base
                                        ? `$${section.rate?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/HC`
                                        : `${section.rate?.toFixed(2)}%`}
                                    </div>
                                    <div className="text-xs text-indigo-500">Calculated rate</div>
                                  </div>
                                </div>
                                <div className="mt-3 text-xs text-gray-500 text-center">
                                  Formula: {formatCurrency(section.pool)} ÷ {section.headcount_base ? Math.round(section.base).toLocaleString() : formatCurrency(section.base)} = {section.headcount_base ? `$${section.rate?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/HC` : `${section.rate?.toFixed(2)}%`}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {(!schedule_a?.sections || schedule_a.sections.length === 0) && (
                    <tr>
                      <td colSpan="5" className="py-4 text-center text-gray-500">
                        No calculation data available. Run GL Calculations first.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Expand/Collapse button */}
            {schedule_a?.sections?.length > 5 && (
              <button
                onClick={() => setScheduleAExpanded(!scheduleAExpanded)}
                className="mt-3 w-full py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg flex items-center justify-center gap-1 transition-colors"
              >
                {scheduleAExpanded ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Show All {schedule_a.sections.length} Rate Sections
                  </>
                )}
              </button>
            )}

          </CardContent>
        </Card>
      </div>

      {/* GL Reconciliation & Division Wrap Rates Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* GL Expense Snapshot */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Database className="w-5 h-5 text-green-600" />
              GL Expense Snapshot
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {gl_reconciliation?.reconciliations?.map((rec, idx) => (
                <div key={idx} className={`flex items-center justify-between py-2 border-b last:border-b-0 ${rec.is_total ? 'border-t-2 border-gray-300 mt-1' : ''}`}>
                  <div className="flex-1">
                    <div className={`text-sm ${rec.is_total ? 'font-bold text-gray-900' : 'font-medium text-gray-800'}`}>{rec.account_series}</div>
                    <div className="text-xs text-gray-500">{rec.description}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm ${rec.is_total ? 'font-bold text-gray-900' : 'font-medium'}`}>{formatCurrency(rec.gl_total)}</div>
                  </div>
                </div>
              ))}
              {(!gl_reconciliation?.reconciliations || gl_reconciliation.reconciliations.length === 0) && (
                <div className="text-center py-4 text-gray-500">
                  Run GL Calculations to see the expense snapshot
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Division Wrap Rates with Interactive Drill-down */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Division Wrap Rates
              <span className="text-xs font-normal text-gray-500 ml-2">(Click to expand)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {divisions.length > 0 ? (
              <div className="space-y-1">
                {/* Division rows */}
                {divisions.filter(d => d.division_id !== 'overall').map((div) => {
                  const divOhRate = div.oh_rate || 0;
                  const divWrap = (1 + fringeRate/100) * (1 + divOhRate/100) * (1 + gaRate/100);
                  const isExpanded = expandedDivision === div.division_id;
                  
                  return (
                    <div key={div.division_id} className="border rounded-lg overflow-hidden">
                      <div 
                        className="flex items-center justify-between py-2 px-3 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => setExpandedDivision(isExpanded ? null : div.division_id)}
                      >
                        <div className="flex items-center gap-2">
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                          <span className="font-medium text-gray-800">{div.division_name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-gray-600">{divOhRate.toFixed(2)}% OH</span>
                          <span className="font-bold text-indigo-600">{divWrap.toFixed(2)}</span>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="px-3 pb-3 pt-1 bg-gray-50 border-t">
                          <div className="text-xs font-semibold text-gray-700 mb-2">Wrap Rate Calculation:</div>
                          <div className="grid grid-cols-5 gap-2 text-center text-xs">
                            <div className="bg-white border rounded p-2">
                              <div className="text-gray-500">(1 + Fringe)</div>
                              <div className="font-bold text-blue-600">{(1 + fringeRate/100).toFixed(4)}</div>
                              <div className="text-gray-400">{fringeRate.toFixed(2)}%</div>
                            </div>
                            <div className="bg-white border rounded p-2">
                              <div className="text-gray-500">×</div>
                              <div className="font-bold text-gray-400 text-base">×</div>
                            </div>
                            <div className="bg-white border rounded p-2">
                              <div className="text-gray-500">(1 + OH)</div>
                              <div className="font-bold text-blue-600">{(1 + divOhRate/100).toFixed(4)}</div>
                              <div className="text-gray-400">{divOhRate.toFixed(2)}%</div>
                            </div>
                            <div className="bg-white border rounded p-2">
                              <div className="text-gray-500">× (1 + G&A)</div>
                              <div className="font-bold text-green-600">{(1 + gaRate/100).toFixed(4)}</div>
                              <div className="text-gray-400">{gaRate.toFixed(2)}%</div>
                            </div>
                            <div className="bg-indigo-100 border border-indigo-200 rounded p-2">
                              <div className="text-indigo-700">= Wrap</div>
                              <div className="font-bold text-indigo-900">{divWrap.toFixed(2)}</div>
                            </div>
                          </div>
                          {div.pool > 0 && (
                            <div className="mt-2 text-xs text-gray-500">
                              Pool: {formatCurrency(div.pool)} | Base: {formatCurrency(div.base)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">
                Division rates will appear after GL calculations are run
              </div>
            )}
          </CardContent>
        </Card>
      </div>


      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <QuickActionButton
              icon={<Database className="w-6 h-6" />}
              label="View GL"
              description="Browse general ledger data"
              onClick={() => onNavigate?.('gl')}
            />
            <QuickActionButton
              icon={<Calculator className="w-6 h-6" />}
              label="Run Calculations"
              description="Execute GL calculations"
              onClick={() => onNavigate?.('gl-calculations')}
              primary
            />
            <QuickActionButton
              icon={<FileText className="w-6 h-6" />}
              label="Schedule H"
              description="View Schedule H report"
              onClick={() => onNavigate?.('schedule-h')}
            />
            <QuickActionButton
              icon={<Shield className="w-6 h-6" />}
              label="DCAA Audit"
              description="Run compliance checks"
              onClick={() => onNavigate?.('audit')}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Sub-components


const ActivityIcon = ({ type }) => {
  switch (type) {
    case 'calculation':
      return <Calculator className="w-4 h-4 text-blue-500" />;
    case 'gl_upload':
      return <Database className="w-4 h-4 text-green-500" />;
    case 'schedule_h':
      return <FileText className="w-4 h-4 text-blue-500" />;
    default:
      return <Activity className="w-4 h-4 text-gray-500" />;
  }
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const QuickActionButton = ({ icon, label, description, onClick, primary }) => (
  <button
    onClick={onClick}
    className={`p-4 rounded-lg border text-left transition-all hover:shadow-md ${
      primary 
        ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700' 
        : 'bg-white border-gray-200 hover:border-blue-300'
    }`}
  >
    <div className={primary ? 'text-white' : 'text-gray-600'}>{icon}</div>
    <div className={`font-semibold mt-2 ${primary ? 'text-white' : 'text-gray-900'}`}>{label}</div>
    <div className={`text-xs mt-1 ${primary ? 'text-blue-100' : 'text-gray-500'}`}>{description}</div>
  </button>
);

export default ExecutiveDashboard;
